import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Search, Wallet, Receipt, RefreshCw, FileSpreadsheet, Loader2, Trash2, FileDown } from "lucide-react";
import { PaymentsTrash } from "@/components/PaymentsTrash";
import { InvoiceSettingsDialog } from "@/components/InvoiceSettingsDialog";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { PeriodFilter, periodRange, rangeParams } from "@/components/PeriodFilter";
import { PaymentsChart } from "@/components/PaymentsChart";

const PAY_STATUS = {
  paid: { label: "مدفوع", cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
  new: { label: "بانتظار الدفع", cls: "bg-amber-500/10 text-amber-600 border-amber-500/20" },
  superseded: { label: "مُستبدل", cls: "bg-muted text-muted-foreground" },
  failed: { label: "فشل", cls: "bg-destructive/10 text-destructive border-destructive/20" },
  expired: { label: "منتهي", cls: "bg-muted text-muted-foreground" },
  canceled: { label: "ملغي", cls: "bg-destructive/10 text-destructive border-destructive/20" },
  refunded: { label: "مسترد", cls: "bg-sky-500/10 text-sky-600 border-sky-500/20" },
  voided: { label: "مُلغى", cls: "bg-muted text-muted-foreground" },
};
const PROVIDERS = { moyasar: "مُيسّر", tamara: "تمارا" };
const FILTERS = [["all", "الكل"], ["paid", "مدفوع"], ["new", "بانتظار الدفع"], ["failed", "فشل/ملغي"]];
const deletable = (p) => !p.hidden && !["paid", "refunded"].includes(p.status);
const fmtDate = (s) => (s ? new Date(s).toLocaleString("ar-SA-u-nu-latn", { dateStyle: "short", timeStyle: "short" }) : "—");

export const AdminPayments = () => {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);
  const [sel, setSel] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [period, setPeriod] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const params = useMemo(() => rangeParams(periodRange(period, from, to)), [period, from, to]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/payments/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const label = period === "custom" ? `${from || "start"}_${to || "now"}` : period;
      a.download = `payments-${label}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("تعذّر تنزيل الملف"); }
    setExporting(false);
  };

  const load = () => api.get("/admin/payments", { params }).then((r) => { setRows(r.data); setSel([]); }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [params]);

  const downloadInvoice = async (p) => {
    try {
      const res = await api.get(`/payments/${p.id}/invoice`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `invoice-${p.transaction_id || p.id}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error("تعذّر تنزيل الفاتورة"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`حذف العملية ${p.transaction_id || ""} من السجل؟`)) return;
    try {
      await api.delete(`/admin/payments/${p.id}`);
      toast.success("تم حذف العملية");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "تعذّر الحذف"); }
  };

  const shown = useMemo(() => rows.filter((p) => {
    if (filter === "failed" && !["failed", "canceled", "expired", "voided"].includes(p.status)) return false;
    if (!["all", "failed"].includes(filter) && p.status !== filter) return false;
    return !q.trim() || (p.transaction_id || "").toLowerCase().includes(q.trim().toLowerCase());
  }), [rows, filter, q]);
  const selectable = shown.filter(deletable);
  const allSel = selectable.length > 0 && selectable.every((p) => sel.includes(p.id));
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSel(allSel ? [] : selectable.map((p) => p.id));
  const bulkDelete = async () => {
    if (!window.confirm(`حذف ${sel.length} عملية غير مدفوعة من السجل؟`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/payments/bulk-delete", { ids: sel });
      toast.success(`تم حذف ${data.deleted} عملية`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "تعذّر الحذف"); }
    setBulkBusy(false);
  };
  const paidTotal = rows.filter((p) => p.status === "paid").reduce((s, p) => s + (p.amount || 0), 0);
  const paidCount = rows.filter((p) => p.status === "paid").length;
  const commissionTotal = rows.reduce((s, p) => s + (p.commission || 0), 0);
  const netTotal = rows.reduce((s, p) => s + (p.net || 0), 0);

  return (
    <div className="space-y-4" data-testid="admin-payments">
      <PeriodFilter period={period} setPeriod={setPeriod} from={from} setFrom={setFrom} to={to} setTo={setTo} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-accent/15 flex items-center justify-center"><Wallet className="w-5 h-5 text-accent" /></div>
          <div><div className="text-xs text-muted-foreground">إجمالي المبالغ المدفوعة</div>
            <div className="text-2xl font-extrabold" data-testid="payments-paid-total">{paidTotal.toFixed(2)} ر.س</div></div>
        </Card>
        <Card className="p-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center"><Receipt className="w-5 h-5 text-primary" /></div>
          <div><div className="text-xs text-muted-foreground">عمليات مدفوعة / الكل</div>
            <div className="text-2xl font-extrabold" data-testid="payments-count">{paidCount} / {rows.length}</div></div>
        </Card>
        <Card className="p-5">
          <div className="text-xs text-muted-foreground">عمولة المنصة (الورش 20 بالمائة · السطحات 5 بالمائة)</div>
          <div className="text-2xl font-extrabold text-emerald-700" data-testid="payments-commission-total">{commissionTotal.toFixed(2)} ر.س</div>
        </Card>
        <Card className="p-5">
          <div className="text-xs text-muted-foreground">صافي مستحقات الورش والسطحات</div>
          <div className="text-2xl font-extrabold" data-testid="payments-net-total">{netTotal.toFixed(2)} ر.س</div>
        </Card>
      </div>
      <PaymentsChart rows={rows} />
      <div className="flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 start-3 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث برقم العملية" className="ps-9" data-testid="payments-search" />
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(([k, l]) => (
            <Button key={k} size="sm" variant={filter === k ? "default" : "outline"} className="rounded-full" onClick={() => setFilter(k)} data-testid={`payments-filter-${k}`}>{l}</Button>
          ))}
          <Button size="icon" variant="ghost" onClick={load} data-testid="payments-refresh"><RefreshCw className="w-4 h-4" /></Button>
          <InvoiceSettingsDialog />
          <PaymentsTrash onRestored={load} />
          <Button size="sm" onClick={exportXlsx} disabled={exporting} className="rounded-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="payments-export-btn">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} تنزيل Excel
          </Button>
        </div>
      </div>
      {sel.length > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-2.5" data-testid="payments-bulk-bar">
          <span className="text-sm font-semibold" data-testid="payments-selected-count">تم تحديد {sel.length} عملية</span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setSel([])} data-testid="payments-clear-selection">إلغاء التحديد</Button>
            <Button size="sm" variant="destructive" className="rounded-full gap-2" disabled={bulkBusy} onClick={bulkDelete} data-testid="payments-bulk-delete-btn">
              {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} حذف المحدد
            </Button>
          </div>
        </div>
      )}
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-10"><Checkbox checked={allSel} onCheckedChange={toggleAll} disabled={!selectable.length} aria-label="تحديد الكل" data-testid="payments-select-all" /></TableHead>
            {["رقم العملية", "التاريخ", "العميل", "النوع", "المبلغ", "الورشة / السطحة", "العمولة", "الصافي", "الحالة", "المزوّد", ""].map((h, i) => <TableHead key={i} className="text-start whitespace-nowrap">{h}</TableHead>)}
          </TableRow></TableHeader>
          <TableBody>
            {shown.map((p) => (
              <TableRow key={p.id} data-testid={`payment-row-${p.id}`} data-state={sel.includes(p.id) ? "selected" : undefined}>
                <TableCell className="w-10">{deletable(p) && <Checkbox checked={sel.includes(p.id)} onCheckedChange={() => toggle(p.id)} data-testid={`payment-select-${p.id}`} />}</TableCell>
                <TableCell className="font-mono text-xs max-w-[220px] truncate" title={p.transaction_id}>{p.transaction_id || "—"}</TableCell>
                <TableCell className="whitespace-nowrap text-sm">{fmtDate(p.paid_at || p.created_at)}</TableCell>
                <TableCell><div className={`font-semibold text-sm ${p.customer_name === "حساب محذوف" ? "text-muted-foreground italic" : ""}`} data-testid={`payment-customer-${p.id}`}>{p.customer_name || "—"}</div><div className="text-xs text-muted-foreground">{p.customer_email}</div></TableCell>
                <TableCell>{p.kind === "repair" ? "إصلاح" : "سطحة"}</TableCell>
                <TableCell className="font-bold whitespace-nowrap">{Number(p.amount || 0).toFixed(2)} ر.س</TableCell>
                <TableCell className="text-sm" data-testid={`payment-payee-${p.id}`}>{p.payee_name || "—"}</TableCell>
                <TableCell className="whitespace-nowrap text-sm text-emerald-700" data-testid={`payment-commission-${p.id}`}>{p.status === "paid" ? <>{p.commission.toFixed(2)} <span className="text-xs text-muted-foreground">({Math.round(p.commission_rate * 100)} بالمائة)</span></> : "—"}</TableCell>
                <TableCell className="whitespace-nowrap text-sm font-semibold" data-testid={`payment-net-${p.id}`}>{p.status === "paid" ? <>{p.net.toFixed(2)} ر.س{p.net <= 2000 && <div className="text-[11px] font-normal text-muted-foreground" data-testid={`payment-bankfee-${p.id}`}>تُخصم الرسوم البنكية</div>}</> : "—"}</TableCell>
                <TableCell><Badge variant="outline" className={(PAY_STATUS[p.status] || {}).cls}>{(PAY_STATUS[p.status] || {}).label || p.status}</Badge></TableCell>
                <TableCell className="whitespace-nowrap text-sm">{PROVIDERS[p.provider] || p.provider}{p.provider === "moyasar" && !p.live ? <span className="text-xs text-muted-foreground"> (تجريبي)</span> : null}</TableCell>
                <TableCell className="w-10">
                  {p.status === "paid" && (
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-primary" onClick={() => downloadInvoice(p)} title="تنزيل الفاتورة" data-testid={`payment-invoice-${p.id}`}>
                      <FileDown className="w-4 h-4" />
                    </Button>
                  )}
                  {!["paid", "refunded"].includes(p.status) && (
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => remove(p)} title="حذف" data-testid={`payment-delete-${p.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {shown.length === 0 && <TableRow><TableCell colSpan={12} className="text-center text-muted-foreground py-8" data-testid="payments-empty">لا توجد عمليات دفع</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};
